import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { ArrowLeft, Edit3, Power, PowerOff, AlertCircle, X, Users, Link2, FileText, Receipt } from "lucide-react";
import PageHeader from "../../../components/PageHeader";
import StatusBadge from "../../../components/StatusBadge";
import { showAdminCompany, updateAdminCompanyStatus, getStatusImpact } from "../../../services/adminCompanies";

const CompanyDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [toast, setToast] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, reason: "", impact: null, loading: false });

  useEffect(() => { load(); }, [id]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await showAdminCompany(id);
      setData(result);
    } catch (err) {
      setError(err.message || "Erro ao carregar empresa");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!data) return;
    const newStatus = !data.company.status;
    if (!newStatus) {
      setConfirmModal({ open: true, reason: "", impact: null, loading: true });
      try {
        const impact = await getStatusImpact(id);
        setConfirmModal(prev => ({ ...prev, impact, loading: false }));
      } catch {
        setConfirmModal(prev => ({ ...prev, impact: null, loading: false }));
      }
    } else {
      setStatusLoading(true);
      try {
        const result = await updateAdminCompanyStatus(id, { status: true, reason: "Ativacao administrativa" });
        setToast({ type: "success", message: "Empresa ativada com sucesso" });
        setData(result);
      } catch (err) {
        setToast({ type: "error", message: err.message || "Erro ao ativar empresa" });
      } finally {
        setStatusLoading(false);
      }
    }
  };

  const confirmDeactivation = async () => {
    if (!confirmModal.reason.trim()) {
      setToast({ type: "error", message: "Motivo e obrigatorio para inativacao" });
      return;
    }
    setStatusLoading(true);
    try {
      const result = await updateAdminCompanyStatus(id, { status: false, reason: confirmModal.reason });
      setToast({ type: "success", message: "Empresa inativada com sucesso" });
      setData(result);
      setConfirmModal({ open: false, reason: "", impact: null, loading: false });
    } catch (err) {
      setToast({ type: "error", message: err.message || "Erro ao inativar empresa" });
    } finally {
      setStatusLoading(false);
    }
  };

  const Section = ({ title, children }) => (
    <div className="neumorphic-card rounded-2xl p-5 border border-gray-200 dark:border-white/10">
      <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">{title}</h3>
      {children}
    </div>
  );

  const Field = ({ label, value }) => (
    <div className="flex justify-between py-1.5 border-b border-gray-100 dark:border-white/5 last:border-0">
      <span className="text-gray-500 text-xs font-semibold">{label}</span>
      <span className="text-gray-900 dark:text-white text-xs font-medium text-right max-w-[60%] truncate" title={typeof value === "string" ? value : undefined}>{value || "\u2014"}</span>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <PageHeader breadcrumb="Clientes & Acessos" title={`Empresa #${id}`} />
        <div className="neumorphic-card rounded-2xl p-6 animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 dark:bg-white/5 rounded w-1/4" />
          <div className="h-4 bg-gray-200 dark:bg-white/5 rounded w-full" />
          <div className="h-4 bg-gray-200 dark:bg-white/5 rounded w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader breadcrumb="Clientes & Acessos" title={`Empresa #${id}`} />
        <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-300 text-xs font-semibold flex items-center gap-3">
          <AlertCircle size={16} /> {error}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const c = data.company || {};
  const addr = c.address || {};
  const readinessBadge = data.fiscal?.ready ? { label: "Pronto", color: "ativo" } : { label: "Incompleto", color: "pendente" };

  return (
    <div className="space-y-4">
      <PageHeader
        breadcrumb="Clientes & Acessos"
        title={`Empresa #${c.id}`}
        subtitle={c.name}
        action={
          <div className="flex items-center gap-2">
            <button onClick={() => history.push(`/companies/${id}/edit`)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500/10 hover:bg-purple-500/20 text-purple-600 dark:text-purple-400 text-xs font-bold rounded-xl border border-purple-500/20">
              <Edit3 size={14} /> Editar
            </button>
            <button onClick={() => history.push("/companies")}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-xs font-bold text-gray-700 dark:text-gray-300 rounded-xl">
              <ArrowLeft size={14} /> Voltar
            </button>
          </div>
        }
      />

      {toast && (
        <div className={`p-3 rounded-xl border text-xs font-semibold flex items-center justify-between ${
          toast.type === "success" ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-300" : "bg-rose-500/10 border-rose-500/20 text-rose-700 dark:text-rose-300"
        }`}>
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={14} /></button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Section title="Dados Institucionais">
          <Field label="Nome" value={c.name} />
          <Field label="Documento" value={c.document} />
          <Field label="E-mail" value={c.email} />
          <Field label="Telefone" value={c.phone} />
        </Section>

        <Section title="Dados Fiscais">
          <Field label="Razao Social" value={c.fiscal?.legalName} />
          <Field label="Nome Fantasia" value={c.fiscal?.tradeName} />
          <Field label="Tipo Pessoa" value={c.fiscal?.personType === "legal_entity" ? "Pessoa Juridica" : c.fiscal?.personType === "individual" ? "Pessoa Fisica" : c.fiscal?.personType} />
          <Field label="CNPJ/CPF Fiscal" value={c.fiscal?.taxId} />
          <Field label="Inscricao Municipal" value={c.fiscal?.municipalRegistration} />
          <Field label="Inscricao Estadual" value={c.fiscal?.stateRegistration} />
          <Field label="Regime Tributario" value={c.fiscal?.taxRegime} />
          <Field label="CNAE" value={c.fiscal?.cnae} />
          <Field label="E-mail Fiscal" value={c.fiscal?.fiscalEmail} />
        </Section>

        <Section title="Endereco">
          <Field label="CEP" value={addr.postalCode} />
          <Field label="Logradouro" value={addr.street} />
          <Field label="Numero" value={addr.number} />
          <Field label="Complemento" value={addr.complement} />
          <Field label="Bairro" value={addr.district} />
          <Field label="Cidade" value={addr.city} />
          <Field label="Estado" value={addr.state} />
        </Section>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Section title="Configuracao">
          <Field label="Timezone" value={c.timezone} />
          <Field label="Observacoes" value={c.internalNotes} />
          <Field label="Ultimo login" value={c.lastLogin ? new Date(c.lastLogin).toLocaleString("pt-BR") : null} />
          <Field label="Criado em" value={c.createdAt ? new Date(c.createdAt).toLocaleString("pt-BR") : null} />
          <Field label="Atualizado em" value={c.updatedAt ? new Date(c.updatedAt).toLocaleString("pt-BR") : null} />
        </Section>

        <Section title="Comercial">
          <Field label="Plano" value={data.commercial?.plan?.name} />
          <Field label="Valor" value={data.commercial?.plan?.amount ? `R$ ${data.commercial.plan.amount}` : null} />
          <Field label="Assinatura" value={data.commercial?.subscription?.available ? "Disponivel" : data.commercial?.subscription?.reason} />
          <Field label="Vencimento" value={c.dueDate ? new Date(c.dueDate).toLocaleDateString("pt-BR") : null} />
        </Section>

        <Section title="Uso">
          <Field label="Usuarios" value={`${data.usage?.users?.active || 0} ativos / ${data.usage?.users?.total || 0} total`} />
          <Field label="Conexoes" value={`${data.usage?.connections?.connected || 0} conectadas / ${data.usage?.connections?.total || 0} total`} />
          <Field label="Filas" value={data.usage?.queues || 0} />
          <Field label="Tickets" value={`${data.usage?.tickets?.open || 0} abertos / ${data.usage?.tickets?.total || 0} total`} />
        </Section>

        <Section title="Fiscal - Prontidao">
          <div className="flex items-center gap-2 mb-3">
            <StatusBadge status={readinessBadge.color} label={readinessBadge.label} />
            <span className="text-[11px] text-gray-400">NFS-e</span>
          </div>
          {data.fiscal?.missingFields?.length > 0 && (
            <div className="text-xs text-amber-600 dark:text-amber-400">
              Campos faltantes: {data.fiscal.missingFields.join(", ")}
            </div>
          )}
          {data.fiscal?.warnings?.length > 0 && (
            <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
              Avisos: {data.fiscal.warnings.join(", ")}
            </div>
          )}
        </Section>
      </div>

      <Section title="Administracao">
        <div className="space-y-0">
          <Field label="Status" value={<StatusBadge status={c.status ? "active" : "inactive"} label={c.status ? "Ativo" : "Inativo"} />} />
          {c.administrativeStatusReason && <Field label="Motivo Status" value={c.administrativeStatusReason} />}
          {c.deactivatedAt && <Field label="Desativado em" value={new Date(c.deactivatedAt).toLocaleString("pt-BR")} />}
          {c.deactivatedBy && <Field label="Desativado por" value={`#${c.deactivatedBy}`} />}
          {c.activatedAt && <Field label="Ativado em" value={new Date(c.activatedAt).toLocaleString("pt-BR")} />}
          {c.activatedBy && <Field label="Ativado por" value={`#${c.activatedBy}`} />}
        </div>
      </Section>

      <div className="flex items-center justify-end pt-2">
        <button onClick={handleToggleStatus} disabled={statusLoading}
          className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-xl border transition-colors ${
            c.status
              ? "bg-rose-500/10 hover:bg-rose-500/20 text-rose-700 dark:text-rose-400 border-rose-500/30"
              : "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/30"
          }`}>
          {c.status ? <><PowerOff size={14} /> Inativar</> : <><Power size={14} /> Ativar</>}
          {statusLoading && "..."}
        </button>
      </div>

      {confirmModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="neumorphic-card rounded-3xl w-full max-w-lg p-6 border border-gray-200 dark:border-white/10 relative space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-white/5 pb-3">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">Confirmar Inativacao</h3>
              <button onClick={() => setConfirmModal({ open: false, reason: "", impact: null, loading: false })} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={18} /></button>
            </div>
            {confirmModal.loading ? (
              <div className="py-6 text-center text-xs text-gray-400">Carregando impacto...</div>
            ) : (
              <>
                {confirmModal.impact && (
                  <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-4 space-y-2">
                    <p className="text-xs font-bold text-amber-700 dark:text-amber-300 flex items-center gap-2">
                      <AlertCircle size={14} /> Impacto da Inativacao
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Users size={12} className="text-purple-500" />
                        {confirmModal.impact.impact?.activeUsers || 0} usuario(s) ativo(s)
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Link2 size={12} className="text-purple-500" />
                        {confirmModal.impact.impact?.activeConnections || 0} conexao(oes) ativa(s)
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <FileText size={12} className="text-purple-500" />
                        {confirmModal.impact.impact?.openTickets || 0} ticket(s) aberto(s)
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Receipt size={12} className="text-purple-500" />
                        Assinatura: {confirmModal.impact.impact?.activeSubscription ? "Ativa" : "Inexistente"}
                      </div>
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Informe o motivo da inativacao da empresa <strong>{c.name}</strong>:
                  </p>
                  <textarea value={confirmModal.reason} onChange={(e) => setConfirmModal(prev => ({ ...prev, reason: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500"
                    rows={3} placeholder="Motivo da inativacao..." />
                </div>
                <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-white/5">
                  <button onClick={() => setConfirmModal({ open: false, reason: "", impact: null, loading: false })}
                    className="px-4 py-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-xs font-semibold text-gray-700 dark:text-gray-300 rounded-xl">Cancelar</button>
                  <button onClick={confirmDeactivation} disabled={statusLoading}
                    className="px-5 py-2 bg-rose-500 hover:bg-rose-600 text-xs font-bold text-white rounded-xl">
                    {statusLoading ? "Processando..." : "Confirmar Inativacao"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDetail;
